'use client';

/**
 * Feature Flag Hook
 *
 * Fetches evaluated feature flags for the current tenant and provides
 * an isEnabled() helper. Flags are cached via React Query with a 60s
 * stale time to match backend TTL.
 */

import { useQuery } from '@tanstack/react-query';
import { useTenant } from '@/lib/tenant-context';
import { useAuth } from '@/lib/auth-context';
import { apiClient } from '@/lib/api-client';

export function useFeatureFlags() {
  const { tenantId } = useTenant();
  const { user } = useAuth();

  const { data: flags = {}, isLoading } = useQuery({
    queryKey: ['featureFlags', tenantId],
    queryFn: () => apiClient.getFeatureFlags(tenantId),
    staleTime: 60 * 1000,
    refetchOnWindowFocus: true,
    retry: 1,
    enabled: !!user,
  });

  return {
    flags,
    isLoading,
    isEnabled: (key: string): boolean => !!flags[key],
  };
}
